'use client'

import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Printer } from 'lucide-react'
import { useShell } from '../components/PageShell'
import { generatePicklistPDF, printPicklist } from '../lib/picklistPrintService'

interface PickList {
  id: string
  name: string
  description: string
  status: string
  created_at: string
  completed_at: string | null
  item_count: number
  picked_count: number
}

interface PickListItem {
  picklist_item_id: string
  item_id: string
  ic: string
  title: string
  item_status: string
  location_id: string
  location_name: string
  tote_id: string
  tote_name: string
  picked_at: string | null
  picked: string
}

export default function PickListsPage() {
  const { toast } = useShell()
  const [searchParams] = useSearchParams()
  const [pickLists, setPickLists] = useState<PickList[]>([])
  const [loading, setLoading] = useState(false)
  const [pickingSlideout, setPickingSlideout] = useState(false)
  const [currentPickList, setCurrentPickList] = useState<PickList | null>(null)
  const [pickListItems, setPickListItems] = useState<PickListItem[]>([])
  const [pickedItems, setPickedItems] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchPickLists()

    // Check if we should auto-start picking
    const pickListId = searchParams.get('id')
    const shouldStart = searchParams.get('start') === 'true'

    if (pickListId && shouldStart) {
      // Find and launch the pick list
      setTimeout(() => {
        launchPicking(pickListId)
      }, 500)
    }
  }, [searchParams])

  const fetchPickLists = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/picklists-pg')
      const data = await response.json()

      if (data.success) {
        setPickLists(data.data)
      }
    } catch (error) {
      console.error('Error fetching pick lists:', error)
    } finally {
      setLoading(false)
    }
  }

  const launchPicking = async (pickListId: string) => {
    try {
      // Fetch pick list details
      const plResponse = await fetch(`/api/picklists-pg`)
      const plData = await plResponse.json()

      if (plData.success) {
        const pl = plData.data.find((p: any) => p.id === pickListId)
        if (pl) {
          setCurrentPickList(pl)

          // Fetch items
          const itemsResponse = await fetch(`/api/picklist-items-pg?picklist_id=${pickListId}`)
          const itemsData = await itemsResponse.json()

          if (itemsData.success) {
            setPickListItems(itemsData.data)

            // Set already picked items - use picklist_item_id for tracking
            const picked = new Set(
              itemsData.data
                .filter((item: PickListItem) => item.picked_at || item.picked === 'completed')
                .map((item: PickListItem) => item.picklist_item_id)
            )
            setPickedItems(picked)

            setPickingSlideout(true)
          }
        }
      }
    } catch (error) {
      console.error('Error launching picking:', error)
    }
  }

  const handlePrintPicklist = async (pickListId: string, pickListName: string) => {
    try {
      // Fetch items for this picklist
      const response = await fetch(`/api/picklists-pg/${pickListId}/items`)
      const data = await response.json()

      if (data.success && data.data) {
        // Map the items to the format expected by the print service
        const items = data.data.map((item: any) => ({
          id: item.id,
          ic: item.ic,
          name: item.title,
          quantity: 1,
          location_name: item.location_name,
          shelf_name: item.shelf_name,
          shelf_code: item.shelf_code,
          tote_name: item.tote_name,
          tote_code: item.tote_code
        }))

        // Generate and print the PDF
        const pdf = generatePicklistPDF(items, `Pick List: ${pickListName}`)
        printPicklist(pdf)

        toast({ title: 'Success', description: 'Picklist sent to printer', type: 'success' })
      } else {
        toast({ title: 'Error', description: 'Failed to fetch picklist items', type: 'error' })
      }
    } catch (error) {
      console.error('Error printing picklist:', error)
      toast({ title: 'Error', description: 'Failed to print picklist', type: 'error' })
    }
  }

  const togglePicked = async (picklistItemId: string) => {
    if (!currentPickList) return

    // Find the item to get the inventory item_id
    const item = pickListItems.find(i => i.picklist_item_id === picklistItemId)
    if (!item) return

    const newPickedItems = new Set(pickedItems)
    const wasPicked = pickedItems.has(picklistItemId)

    if (wasPicked) {
      newPickedItems.delete(picklistItemId)
    } else {
      newPickedItems.add(picklistItemId)
    }

    setPickedItems(newPickedItems)

    // Update on server
    try {
      const response = await fetch('/api/update-picked-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          picklist_id: currentPickList.id,
          item_id: item.item_id,
          picked: !wasPicked
        })
      })

      const data = await response.json()

      if (!data.success) {
        toast({ title: 'Error', description: 'Failed to update picked status', type: 'error' })
        // Revert the change
        setPickedItems(pickedItems)
      }
    } catch (error) {
      console.error('Error updating picked status:', error)
      toast({ title: 'Error', description: 'Failed to update picked status', type: 'error' })
      // Revert the change
      setPickedItems(pickedItems)
    }
  }

  const handleDone = async () => {
    if (!currentPickList) return

    const unpickedCount = pickListItems.length - pickedItems.size

    if (unpickedCount === 0) {
      // Complete immediately
      await completePickList(false)
    } else {
      // Show confirmation - for now just complete anyway
      if (confirm(`${unpickedCount} items unpicked. Complete anyway?`)) {
        await completePickList(true)
      }
    }
  }

  const completePickList = async (force: boolean) => {
    if (!currentPickList) return

    try {
      const response = await fetch('/api/complete-picklist', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pick_list_id: currentPickList.id,
          force_complete: force
        })
      })

      const data = await response.json()

      if (data.success) {
        toast({
          title: 'Pick List Completed',
          description: `${currentPickList.name} has been completed successfully`,
          type: 'success'
        })
        setPickingSlideout(false)
        fetchPickLists()
      } else {
        toast({ title: 'Error', description: data.error || 'Failed to complete pick list', type: 'error' })
      }
    } catch (error) {
      console.error('Error completing pick list:', error)
      toast({ title: 'Error', description: 'Failed to complete pick list', type: 'error' })
    }
  }

  const handleUpdate = async () => {
    toast({
      title: 'Progress Saved',
      description: `${pickedItems.size} of ${pickListItems.length} items marked as picked`,
      type: 'success'
    })
    fetchPickLists()
  }

  // Group items by location -> tote
  const groupedItems = pickListItems.reduce((acc, item) => {
    const locKey = item.location_id || 'unknown'
    const toteKey = item.tote_id || 'unknown'

    if (!acc[locKey]) {
      acc[locKey] = {
        name: item.location_name || 'Unknown Location',
        totes: {}
      }
    }

    if (!acc[locKey].totes[toteKey]) {
      acc[locKey].totes[toteKey] = {
        name: item.tote_name || 'Unknown Tote',
        items: []
      }
    }

    acc[locKey].totes[toteKey].items.push(item)

    return acc
  }, {} as Record<string, { name: string; totes: Record<string, { name: string; items: PickListItem[] }> }>)

  const pickedCount = pickedItems.size
  const totalCount = pickListItems.length
  const progressPercent = totalCount > 0 ? Math.round((pickedCount / totalCount) * 100) : 0

  return (
    <div suppressHydrationWarning style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Title */}
        <h1 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '48px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#FF6B35',
          textShadow: '0 0 20px rgba(255, 107, 53, 0.5), 0 0 40px rgba(255, 107, 53, 0.3)',
          marginBottom: '24px'
        }}>
          Pick Lists
        </h1>

        {/* Pick Lists Table */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(36, 41, 56, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 107, 53, 0.2)',
          borderRadius: '12px',
          boxShadow: '0 0 30px rgba(255, 107, 53, 0.2)',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Corner accent pattern */}
          <div style={{ position: 'absolute', top: 0, right: 0, width: '128px', height: '128px', pointerEvents: 'none', background: 'linear-gradient(to bottom right, transparent, transparent, rgba(255, 107, 53, 0.1))' }} />

          <div style={{ position: 'relative', zIndex: 10 }}>
            {loading ? (
              <div style={{ textAlign: 'center', color: 'rgba(224, 230, 240, 0.7)', fontFamily: 'Rajdhani, sans-serif', fontSize: '16px', padding: '40px' }}>Loading...</div>
            ) : pickLists.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgba(156, 163, 175, 0.7)', fontFamily: 'Rajdhani, sans-serif', fontSize: '16px', padding: '40px' }}>No pick lists found</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 107, 53, 0.2)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Items</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Progress</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pickLists.map(pl => (
                    <tr key={pl.id} style={{ borderBottom: '1px solid rgba(156, 163, 175, 0.1)' }}>
                      <td style={{ padding: '16px', color: '#ffffff', fontFamily: 'Rajdhani, sans-serif', fontSize: '15px', fontWeight: 500 }}>{pl.name}</td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: 'Rajdhani, sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          background: pl.status === 'active' ? 'rgba(34, 197, 94, 0.15)' :
                                     pl.status === 'completed' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(100, 116, 139, 0.15)',
                          color: pl.status === 'active' ? '#22c55e' :
                                 pl.status === 'completed' ? '#3b82f6' : '#94a3b8',
                          border: `1px solid ${pl.status === 'active' ? 'rgba(34, 197, 94, 0.3)' :
                                               pl.status === 'completed' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(100, 116, 139, 0.3)'}`
                        }}>
                          {pl.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px', color: '#ffffff', fontFamily: 'Rajdhani, sans-serif', fontSize: '15px', fontWeight: 500 }}>{pl.item_count || 0}</td>
                      <td style={{ padding: '16px', color: '#ffffff', fontFamily: 'Rajdhani, sans-serif', fontSize: '15px', fontWeight: 500 }}>
                        <span style={{ color: '#FF6B35', fontWeight: 600 }}>{pl.picked_count || 0}</span> / {pl.item_count || 0}
                      </td>
                      <td style={{ padding: '16px', color: 'rgba(156, 163, 175, 0.9)', fontFamily: 'Rajdhani, sans-serif', fontSize: '14px' }}>
                        {new Date(pl.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '16px' }}>
                        {pl.status === 'active' && (
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => handlePrintPicklist(pl.id, pl.name)} style={{
                              padding: '10px 20px',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              background: '#242938',
                              border: '2px solid #00F0FF',
                              color: '#00F0FF',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              borderRadius: '6px'
                            }}>
                              <Printer size={18} />
                              Print
                            </button>
                            <button onClick={() => launchPicking(pl.id)} style={{
                            padding: '10px 20px',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            borderRadius: '6px',
                            boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)'
                            e.currentTarget.style.boxShadow = '0 0 30px rgba(34, 197, 94, 0.5)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.3)'
                          }}>
                            Launch
                          </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Picking Interface Slideout */}
      {pickingSlideout && currentPickList && (
        <>
          <div onClick={() => setPickingSlideout(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            zIndex: 999
          }} />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '900px',
            height: '100vh',
            background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
            borderLeft: '1px solid rgba(255, 107, 53, 0.3)',
            boxShadow: '-4px 0 30px rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(12px)',
              borderBottom: '1px solid rgba(255, 107, 53, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  color: '#FF6B35',
                  fontSize: '32px',
                  fontWeight: 700,
                  fontFamily: 'Rajdhani, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
                }}>
                  Picking Interface
                </h2>
                <div style={{
                  color: 'rgba(156, 163, 175, 0.9)',
                  fontSize: '16px',
                  fontWeight: 500,
                  fontFamily: 'Rajdhani, sans-serif',
                  marginTop: '4px'
                }}>
                  {currentPickList.name}
                </div>
              </div>
              <button onClick={() => setPickingSlideout(false)} style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)'
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'
                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
              }}>
                Close
              </button>
            </div>

            {/* Progress Bar */}
            <div style={{
              padding: '20px 24px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderBottom: '1px solid rgba(255, 107, 53, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <span style={{
                  color: 'rgba(156, 163, 175, 0.9)',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Rajdhani, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Progress
                </span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span style={{
                    color: '#ffffff',
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>
                    <span style={{ color: '#FF6B35' }}>{pickedCount}</span> / {totalCount} items
                  </span>
                  <span style={{
                    color: progressPercent < 70 ? '#22c55e' : progressPercent < 90 ? '#f59e0b' : '#ef4444',
                    fontSize: '18px',
                    fontWeight: 700,
                    fontFamily: 'Rajdhani, sans-serif'
                  }}>
                    {progressPercent}%
                  </span>
                </div>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                background: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '999px',
                overflow: 'hidden',
                border: '1px solid rgba(156, 163, 175, 0.2)'
              }}>
                <div style={{
                  height: '100%',
                  background: progressPercent < 70
                    ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
                    : progressPercent < 90
                    ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                    : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                  transition: 'width 0.5s ease, background 0.3s ease',
                  width: `${progressPercent}%`,
                  borderRadius: '999px'
                }} />
              </div>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {Object.keys(groupedItems).map(locKey => {
                const location = groupedItems[locKey]
                const locItems = Object.values(location.totes).flatMap(t => t.items)
                const locPicked = locItems.filter(i => pickedItems.has(i.id)).length

                return (
                  <div key={locKey} style={{
                    background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.6) 0%, rgba(36, 41, 56, 0.6) 100%)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(6, 182, 212, 0.2)',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 0 20px rgba(6, 182, 212, 0.1)'
                  }}>
                    {/* Location Header */}
                    <div style={{
                      background: 'rgba(6, 182, 212, 0.1)',
                      padding: '16px 20px',
                      borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        color: '#06b6d4',
                        fontSize: '18px',
                        fontWeight: 700,
                        fontFamily: 'Rajdhani, sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        {location.name}
                      </span>
                      <span style={{
                        color: 'rgba(156, 163, 175, 0.9)',
                        fontSize: '13px',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontWeight: 600
                      }}>
                        {locPicked}/{locItems.length} picked
                      </span>
                    </div>

                    {/* Totes */}
                    {Object.keys(location.totes).map(toteKey => {
                      const tote = location.totes[toteKey]
                      const totePicked = tote.items.filter(i => pickedItems.has(i.picklist_item_id)).length

                      return (
                        <div key={toteKey} style={{
                          borderBottom: '1px solid rgba(156, 163, 175, 0.1)'
                        }}>
                          {/* Tote Header */}
                          <div style={{
                            background: 'rgba(255, 107, 53, 0.1)',
                            padding: '14px 20px',
                            borderBottom: '1px solid rgba(255, 107, 53, 0.2)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{
                              color: '#FF6B35',
                              fontSize: '16px',
                              fontWeight: 700,
                              fontFamily: 'Rajdhani, sans-serif',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              {tote.name}
                            </span>
                            <span style={{
                              color: 'rgba(156, 163, 175, 0.9)',
                              fontSize: '13px',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontWeight: 600
                            }}>
                              {totePicked}/{tote.items.length} picked
                            </span>
                          </div>

                          {/* Items */}
                          {tote.items.map(item => {
                            const isPicked = pickedItems.has(item.picklist_item_id)

                            return (
                              <div key={item.picklist_item_id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '16px 20px',
                                background: isPicked ? 'rgba(34, 197, 94, 0.1)' : 'rgba(0, 0, 0, 0.2)',
                                borderBottom: '1px solid rgba(156, 163, 175, 0.1)',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isPicked) {
                                  e.currentTarget.style.background = 'rgba(255, 107, 53, 0.05)'
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isPicked) {
                                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)'
                                }
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isPicked}
                                  onChange={() => togglePicked(item.picklist_item_id)}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    marginRight: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#22c55e'
                                  }}
                                />
                                <div style={{ flex: 1, display: 'flex', gap: '16px', alignItems: 'center' }}>
                                  <span style={{
                                    color: '#FF6B35',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    minWidth: '100px',
                                    fontFamily: 'Rajdhani, sans-serif',
                                    fontVariantNumeric: 'tabular-nums'
                                  }}>
                                    {item.ic}
                                  </span>
                                  <span style={{
                                    color: isPicked ? 'rgba(224, 230, 240, 0.7)' : '#ffffff',
                                    fontSize: '15px',
                                    flex: 1,
                                    fontFamily: 'Rajdhani, sans-serif',
                                    fontWeight: 500,
                                    textDecoration: isPicked ? 'line-through' : 'none'
                                  }}>
                                    {item.title}
                                  </span>
                                  <span style={{
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    minWidth: '90px',
                                    textAlign: 'center',
                                    fontFamily: 'Rajdhani, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                    background: item.item_status === 'active' ? 'rgba(34, 197, 94, 0.15)' :
                                              item.item_status === 'ready' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                    color: item.item_status === 'active' ? '#22c55e' :
                                          item.item_status === 'ready' ? '#3b82f6' : '#ef4444',
                                    border: `1px solid ${item.item_status === 'active' ? 'rgba(34, 197, 94, 0.3)' :
                                                         item.item_status === 'ready' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                  }}>
                                    {item.item_status}
                                  </span>
                                  {isPicked && (
                                    <span style={{
                                      color: '#22c55e',
                                      fontSize: '18px',
                                      fontWeight: 'bold',
                                      marginLeft: '8px'
                                    }}>
                                      ✓
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div style={{
              padding: '24px',
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(12px)',
              borderTop: '1px solid rgba(255, 107, 53, 0.2)',
              display: 'flex',
              gap: '16px'
            }}>
              <button onClick={handleDone} style={{
                flex: 1,
                padding: '16px 24px',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)'
                e.currentTarget.style.boxShadow = '0 0 30px rgba(34, 197, 94, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.3)'
              }}>
                Done
              </button>
              <button onClick={handleUpdate} style={{
                flex: 1,
                padding: '16px 24px',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '8px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)'
                e.currentTarget.style.boxShadow = '0 0 30px rgba(6, 182, 212, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = '0 0 20px rgba(6, 182, 212, 0.3)'
              }}>
                Update Pick List
              </button>
              <button onClick={() => setPickingSlideout(false)} style={{
                flex: 1,
                padding: '16px 24px',
                border: '1px solid rgba(100, 116, 139, 0.3)',
                borderRadius: '8px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                boxShadow: '0 0 20px rgba(100, 116, 139, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)'
                e.currentTarget.style.boxShadow = '0 0 30px rgba(100, 116, 139, 0.5)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = '0 0 20px rgba(100, 116, 139, 0.3)'
              }}>
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
